import { createContext, FC, PropsWithChildren, useContext, useMemo } from 'react';

import {
  LoginStatistics,
  ReportStatistics,
  ReportStatisticsCreate,
} from '@src/main/modules/common/statistics/domain/Statistics';
import { StatisticsRepository } from '@src/main/modules/common/statistics/domain/StatisticsRepository';
import { useStatisticsUseCases } from '@src/main/ui/providers/statistics/hooks/useStatisticsUseCase';

interface ContextState {
  loginStatistics: LoginStatistics[] | undefined;
  reportStatistics: ReportStatistics[] | undefined;
  createReportStatistics: (reportStatistics: ReportStatisticsCreate) => void;
}

const StatisticsContext = createContext<ContextState>({} as ContextState);

interface Props {
  statisticsRepository: StatisticsRepository;
}

export const StatisticsContextProvider: FC<PropsWithChildren<Props>> = ({
  children,
  statisticsRepository,
}) => {
  const { loginStatistics, reportStatistics, createReportStatistics } =
    useStatisticsUseCases(statisticsRepository);

  const values = useMemo(
    () => ({
      loginStatistics,
      reportStatistics,
      createReportStatistics,
    }),
    [loginStatistics, reportStatistics, createReportStatistics],
  );

  return <StatisticsContext.Provider value={values}>{children}</StatisticsContext.Provider>;
};

export const useStatisticsContext = (): ContextState => useContext(StatisticsContext);
